import React from 'react';

import http from 'api/http';
import { boatAPI, trailerAPI } from 'api/requests';

import { consoleError } from 'utils';

import Header from './Header';
import GeneralInformation from './GeneralInformation';
import ListOfModels from './ListOfModels';
import NotesSection from './Notes';
import ReportDocumentation from './ReportDocumentation';
import ApplicationActions from 'Components/ApplicationActions';

import { ROLES } from 'constants/roles';
import { STATUS } from 'constants/index';

export default class New extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      id: '',
      description: '',
      status: '',
      year: '',
      inspectionDate: '',
      documents: [],
      yearList: [],
      errors: {},
      modelList: [],
      selectedModelList: [],
      requestInProcess: false,
      address:'',
    };
  }

  componentDidMount() {
    const {user} = this.props;

    const $$getMethod = () => {
      if (user.getActiveRole() === ROLES.BOAT_MANUFACTURER.serverName) {
        return boatAPI.modelYears;
      }

      if (user.getActiveRole() === ROLES.TRAILER_MANUFACTURER.serverName) {
        return trailerAPI.getPermissibleModelYears;
      }
    };

    http.get($$getMethod())
      .then(({data: yearList}) => {
        this.setState({yearList});
      });
  }

  handleSelect = key => value => {
    const {errors} = this.state;
    const {markStepAsRedacted} = this.props;

    delete errors[key];
    markStepAsRedacted();

    this.setState({[key]: value, errors});
  };

  handleInputChange = key => value => {
    const {errors} = this.state;
    const {markStepAsRedacted} = this.props;

    delete errors[key];
    markStepAsRedacted();

    this.setState({[key]: value, errors});
  };

  handleYearSelect = year => {
    const {errors} = this.state;
    const {markStepAsRedacted, user} = this.props;

    const $$getMethod = year => {
      if (user.getActiveRole() === ROLES.BOAT_MANUFACTURER.serverName) {
        return boatAPI.approvedBoatsByYear(year);
      }

      if (user.getActiveRole() === ROLES.TRAILER_MANUFACTURER.serverName) {
        return trailerAPI.trailersByYear(year, true);
      }
    };

    delete errors.year;
    markStepAsRedacted();

    this.setState({year, errors}, () => {
      let modelList = [];
      if (year) {
        http.get($$getMethod(year))
          .then(({data: approvedList}) => {
            modelList = approvedList;
          })
          .finally(() => {
            this.setState({modelList, selectedModelList: []});
          });
      } else {
        this.setState({modelList, selectedModelList: []});
      }
    });
  };

  handleSelectFromList = model => {
    const {modelList, errors} = this.state;
    const {markStepAsRedacted} = this.props;
    const selectedModelIndex = modelList.findIndex(element => +model.id === +element.id);
    modelList.splice(selectedModelIndex, 1);

    delete errors.selectedModelList;
    markStepAsRedacted();

    this.setState(prevState => ({
      selectedModelList: [...prevState.selectedModelList, model],
      modelList,
      errors
    }));
  };

  handleRemoveFromList = (model, index) => () => {
    const {modelList, selectedModelList, errors} = this.state;
    const {markStepAsRedacted} = this.props;

    selectedModelList.splice(index, 1);
    modelList.push(model);

    if (!selectedModelList.length) errors.selectedModelList = 'At least one model needs to be selected.';

    markStepAsRedacted();

    this.setState({modelList, selectedModelList, errors});
  };

  handleFileSelect = addedFiles => {
    const {errors} = this.state;
    const {markStepAsRedacted} = this.props;

    delete errors.documents;

    markStepAsRedacted();

    this.setState(prevState => ({documents: [...prevState.documents, ...addedFiles], errors}));
  };

  handleFileRemove = fileIndex => () => {
    const {documents, errors} = this.state;
    const {markStepAsRedacted} = this.props;

    documents.splice(fileIndex, 1);
    if (!documents.length) {
      errors.documents = true;
    }

    markStepAsRedacted();

    this.setState({documents, errors});
  };

  _handleSubmitReportClick = () => {
    const {status, year, description, inspectionDate, selectedModelList, documents, notes, address} = this.state;
    const {user, handleSubmitReport, inspectionId} = this.props;

    const $$getRequiredFields = () => {
      const fields = ['description', 'address'];

      if (status === '') {
        fields.push('year', 'inspectionDate', 'selectedModelList');
        if (!user.isInternal()) fields.push('documents');
      } else if (status === STATUS.INCOMPLETE) {
        fields.push('year', 'inspectionDate', 'selectedModelList');
        if (!user.isInternal()) fields.push('documents');
      }

      return fields;
    };

    const $$validate = () => {
      return new Promise((resolve, reject) => {
        const {errors} = this.state;
        const fields = $$getRequiredFields();

        let hasErrors = !!Object.keys(errors).length;

        for (let field of fields) {
          const data = this.state[field];

          if (data.length !== undefined ? !data.length : !data) {
            if (field === 'selectedModelList') errors.selectedModelList = 'At least one model needs to be selected.';
            else errors[field] = true;
            hasErrors = true;
          }
        }

        if (hasErrors) {
          this.setState({errors}, reject);
        } else {
          resolve(user.getActiveRole() === ROLES.BOAT_MANUFACTURER.serverName);
        }
      });
    };

    $$validate()
      .then(boatInspecFlag => {
        const requestJson = {
          inspectionDate: inspectionDate.utc(),
          modelIds: selectedModelList.map(model => model.id),
          year,
          description,
          notes,
          boatInspecFlag,
          ...address.value
        };

        handleSubmitReport(inspectionId, requestJson, documents);
      })
      .catch(() => {
        consoleError('Report has errors');
      });
  };

  render() {
    const {description, inspectionDate, year, notes, documents, yearList, errors, selectedModelList,
      modelList, address} = this.state;
    const {user, handleGoBackClick, getDetailsLink, manufacturerAddresses} = this.props;

    return (
      <section className='scene__scene-wrapper'>
        <section className='scene__content-wrapper'>
          <Header handleGoBackClick={handleGoBackClick} />
          <GeneralInformation
            errors={errors}
            description={description}
            inspectionDate={inspectionDate}
            year={year}
            yearList={yearList}
            address={address}
            manufacturerAddresses={manufacturerAddresses}
            handleSelect={this.handleSelect}
            handleInputChange={this.handleInputChange}
            handleYearSelect={this.handleYearSelect}
          />
          <ListOfModels
            selectedModelList={selectedModelList}
            modelList={modelList}
            getDetailsLink={getDetailsLink}
            errors={errors}
            handleSelectFromList={this.handleSelectFromList}
            handleRemoveFromList={this.handleRemoveFromList}
          />
          <NotesSection notes={notes} user={user} handleInputChange={this.handleInputChange} />
          <ReportDocumentation
            documents={documents}
            user={user}
            errors={errors}
            inspectionDate={inspectionDate}
            handleFileSelect={this.handleFileSelect}
            handleFileRemove={this.handleFileRemove}
          />

          <ApplicationActions
            backButtonLabel='Cancel'
            saveButtonLabel='Submit'
            saveButtonAction={this._handleSubmitReportClick}
            backButtonAction={handleGoBackClick}
          />
        </section>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/Scenes/Inspection/New/index.jsx